import { noop } from 'lodash-es';
import { memo } from 'react';

import { Action } from '../../types/Action';
import { ButtonSize } from '../../types/SharedButtonProps';
import { SharedIconProps } from '../../types/SharedIconProps';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { Button } from '../Button/Button';
import { IconButton } from '../Button/subcomponents/IconButton';
import { resolveButtonIconSize } from '../Button/utils';
import { Layout } from '../Layout/Layout';
import { SplitButton } from '../SplitButton/SplitButton';

export interface ActionsProps {
  actions: Action[];
  mode?: 'icon';
  moreActions?: {
    actions: Action[];
    icon: SharedIconProps;
    onOpenChange?: (open: boolean) => void;
  };
  size?: ButtonSize;
}

export const Actions = memo(
  ({ actions, mode, moreActions, size = 'm' }: ActionsProps) => {
    return (
      <Layout preset="buttons" role="toolbar" spacing={size}>
        {actions.map((action) => {
          if (action.mode === 'split' && action.menu) {
            const {
              current: _current,
              mode: _mode,
              onToggle: _onToggle,
              selected: _selected,
              status: _status,
              menu,
              ...primaryProps
            } = action;

            return (
              <SplitButton
                key={action.label}
                {...primaryProps}
                menu={menu}
                size={size}
              />
            );
          }

          const usesToggle = action.onToggle !== undefined;
          const tooltip = {
            direction: action.tooltip?.direction,
            text:
              action.tooltip?.text ??
              (mode === 'icon' ? action.label : undefined),
          };

          if (mode === 'icon') {
            if (!action.icon) {
              return null;
            }

            return (
              <IconButton
                aria-current={action.current ? true : undefined}
                disabled={action.disabled}
                download={action.download}
                href={action.href}
                icon={action.icon}
                id={action.id}
                isLoading={action.isLoading}
                key={action.label}
                label={action.label}
                rel={action.rel}
                selected={usesToggle ? (action.selected ?? false) : undefined}
                size={size}
                status={usesToggle ? action.status : undefined}
                target={action.target}
                tooltip={tooltip}
                variant="tertiary"
                onClick={usesToggle ? undefined : (action.onClick ?? noop)}
                onToggle={usesToggle ? action.onToggle : undefined}
              />
            );
          }

          return (
            <Button
              disabled={action.disabled}
              download={action.download}
              href={action.href}
              icon={action.icon}
              id={action.id}
              isLoading={action.isLoading}
              key={action.label}
              label={action.label}
              rel={action.rel}
              selected={usesToggle ? (action.selected ?? false) : undefined}
              size={size}
              status={usesToggle ? action.status : undefined}
              target={action.target}
              text={action.text}
              tooltip={tooltip}
              variant={action.variant}
              onClick={usesToggle ? undefined : (action.onClick ?? noop)}
              onToggle={usesToggle ? action.onToggle : undefined}
            />
          );
        })}
        {moreActions && (
          <ActionsMenu
            {...moreActions}
            icon={{
              ...moreActions.icon,
              size: resolveButtonIconSize({ isIconMode: true, size }),
            }}
            size={size}
          />
        )}
      </Layout>
    );
  },
);

Actions.displayName = 'Actions';
